import React, { useState } from 'react';
import signsealLogo from '../signseal_logo.png';
import './PublicSurveyPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function PublicSurveyPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [startTime] = useState(Date.now()); // Track when form was loaded

  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    labelQuality: '',
    turnaroundTime: '',
    teamResponsiveness: '',
    deliveryOnTime: null,
    deliveryExplanation: '',
    suggestions: '',
    overallRating: null,
    recommendationScore: null,
    mayContact: false,
    contactEmail: '',
    contactPhone: '',
    website: '' // Honeypot field - should remain empty
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, overallRating: rating }));
  };

  const handleNPSClick = (score) => {
    setFormData(prev => ({ ...prev, recommendationScore: score }));
  };

  const renderStars = () => {
    return [1, 2, 3, 4, 5, 6].map(star => (
      <button
        key={star}
        type="button"
        onClick={() => handleRatingClick(star)}
        className={`star ${formData.overallRating >= star ? 'filled' : ''}`}
      >
        ★
      </button>
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    // Validate required fields
    if (!formData.labelQuality || !formData.turnaroundTime || !formData.teamResponsiveness || formData.deliveryOnTime === null) {
      setError('Please complete all required fields');
      setSubmitting(false);
      return;
    }

    // Calculate time elapsed since form loaded
    const timeElapsed = Math.floor((Date.now() - startTime) / 1000); // in seconds

    try {
      const response = await fetch(`${API_URL}/api/survey/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          timeElapsed
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit survey');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="public-survey-container">
        <div className="survey-card">
          <img src={signsealLogo} alt="SignSeal Logo" className="survey-logo" />
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Thank You!</h2>
            <p>Your feedback has been submitted successfully.</p>
            <p className="success-subtext">Your input helps us serve you better.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-survey-container">
      <div className="survey-card">
        <img src={signsealLogo} alt="SignSeal Logo" className="survey-logo" />
        
        <h1 className="survey-title">Customer Satisfaction Survey</h1>
        <p className="survey-intro">We value your feedback! Please take a moment to answer a few quick questions to help us improve our service.</p>

        {error && <div className="survey-error">{error}</div>}

        <form onSubmit={handleSubmit} className="survey-form">
          
          {/* Honeypot field - hidden from users, catches bots */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Optional Company Name */}
          <div className="form-group">
            <label>Company Name (Optional)</label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Your company name"
            />
          </div>

          {/* Contact Person */}
          <div className="form-group">
            <label>Who was your contact person? (Optional)</label>
            <input
              type="text"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              placeholder="Name of person you dealt with"
            />
          </div>

          {/* Label Quality */}
          <div className="form-group required">
            <label>How satisfied are you with the quality of the labels you received?</label>
            <div className="radio-group">
              {['Very Satisfied', 'Satisfied', 'Neutral', 'Dissatisfied', 'Very Dissatisfied'].map(option => (
                <label key={option} className="radio-option">
                  <input
                    type="radio"
                    name="labelQuality"
                    value={option}
                    checked={formData.labelQuality === option}
                    onChange={handleChange}
                    required
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Turnaround Time */}
          <div className="form-group required">
            <label>How would you rate our turnaround time on your order?</label>
            <div className="radio-group">
              {['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'].map(option => (
                <label key={option} className="radio-option">
                  <input
                    type="radio"
                    name="turnaroundTime"
                    value={option}
                    checked={formData.turnaroundTime === option}
                    onChange={handleChange}
                    required
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Team Responsiveness */}
          <div className="form-group required">
            <label>How responsive and helpful was our team during your order process?</label>
            <div className="radio-group">
              {['Very Helpful', 'Helpful', 'Neutral', 'Unhelpful', 'Very Unhelpful'].map(option => (
                <label key={option} className="radio-option">
                  <input
                    type="radio"
                    name="teamResponsiveness"
                    value={option}
                    checked={formData.teamResponsiveness === option}
                    onChange={handleChange}
                    required
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Delivery On Time */}
          <div className="form-group required">
            <label>Was your order delivered correctly and on time?</label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="deliveryOnTime"
                  value="true"
                  checked={formData.deliveryOnTime === true}
                  onChange={() => setFormData(prev => ({ ...prev, deliveryOnTime: true, deliveryExplanation: '' }))}
                  required
                />
                <span>Yes</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="deliveryOnTime"
                  value="false"
                  checked={formData.deliveryOnTime === false}
                  onChange={() => setFormData(prev => ({ ...prev, deliveryOnTime: false }))}
                  required
                />
                <span>No</span>
              </label>
            </div>
            {formData.deliveryOnTime === false && (
              <input
                type="text"
                name="deliveryExplanation"
                value={formData.deliveryExplanation}
                onChange={handleChange}
                placeholder="Please explain..."
                className="explanation-input"
              />
            )}
          </div>

          {/* Overall Rating */}
          <div className="form-group">
            <label>Overall, how would you rate your experience with SignSeal?</label>
            <div className="star-rating">
              {renderStars()}
            </div>
          </div>

          {/* Net Promoter Score */}
          <div className="form-group">
            <label>How likely are you to recommend SignSeal to others?</label>
            <div className="nps-scale">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(score => (
                <button
                  key={score}
                  type="button"
                  className={`nps-button ${formData.recommendationScore === score ? 'selected' : ''}`}
                  onClick={() => handleNPSClick(score)}
                >
                  {score}
                </button>
              ))}
            </div>
            <div className="nps-labels">
              <span>Not likely</span>
              <span>Very likely</span>
            </div>
          </div>

          {/* Suggestions */}
          <div className="form-group">
            <label>Any suggestions on how we can improve our products or service?</label>
            <textarea
              name="suggestions"
              value={formData.suggestions}
              onChange={handleChange}
              rows="4"
              placeholder="Your suggestions..."
            />
          </div>

          {/* Contact Permission */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="mayContact"
                checked={formData.mayContact}
                onChange={handleChange}
              />
              <span>May we contact you about your feedback?</span>
            </label>
          </div>

          {formData.mayContact && (
            <div className="contact-fields">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  placeholder="Your phone number"
                />
              </div>
            </div>
          )}

          <button type="submit" className="submit-button" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Survey'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PublicSurveyPage;
